import React from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { CalendarIcon, MapPinIcon, ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

const featureCards = [
  {
    icon: CalendarIcon,
    title: 'Find Local Events',
    description: 'Browse upcoming garage sales in your area and plan your weekend treasure hunting.',
  },
  {
    icon: MapPinIcon,
    title: 'Submit Your Sale',
    description: 'Create a listing for your garage sale and reach more potential customers in your area.',
  },
  {
    icon: MagnifyingGlassIcon,
    title: 'Search & Filter',
    description: 'Find exactly what you\'re looking for with our powerful search and filtering tools.',
  },
]

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } },
}

const HomePage = () => {
  usePageTitle('Find Local Garage Sales Near You')
  return (
    <div className="bg-bg">
      {/* Hero section */}
      <div className="relative bg-primary-800">
        <div className="absolute inset-0">
          <picture>
            <source
              type="image/avif"
              srcSet="/images/hero-garage-sale-mobile.avif 960w, /images/hero-garage-sale.avif 1920w"
              sizes="100vw"
            />
            <img
              className="w-full h-full object-cover"
              src="/images/hero-garage-sale-fallback.jpg"
              alt="Neighborhood garage sale scene"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl drop-shadow-lg">
              Discover Hidden Treasures
            </h1>
            <p className="mt-6 text-xl text-white/90 max-w-3xl mx-auto drop-shadow-lg">
              Find unique items at local garage sales. Connect with your community and turn someone's old treasures into your new favorites.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/events"
                  className="inline-flex items-center justify-center px-6 py-3 min-h-[48px]
                    border border-transparent text-base font-medium rounded-lg
                    text-white bg-primary-600 hover:bg-primary-500
                    shadow-md hover:shadow-lg
                    transition-all duration-200"
                >
                  Browse Events
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/events/new"
                  className="inline-flex items-center justify-center px-6 py-3 min-h-[48px]
                    border-2 border-white/50 text-base font-medium rounded-lg
                    text-white hover:bg-white/15 backdrop-blur-sm
                    transition-all duration-200"
                >
                  Host a Sale
                  <CalendarIcon className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Feature section */}
      <div className="py-16 sm:py-20 bg-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Why Choose Us</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-text sm:text-4xl">
              Your Community's Garage Sale Hub
            </p>
            <p className="mt-4 max-w-2xl text-xl text-text-secondary mx-auto">
              Everything you need to find great deals or host a successful sale
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="mt-16"
          >
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((feature) => (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="relative card p-6 hover:shadow-md hover:border-border-strong
                    transition-all duration-200"
                >
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl
                    bg-primary-500 text-white mb-4
                    shadow-sm">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-text">{feature.title}</h3>
                  <p className="mt-2 text-base text-text-secondary">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default HomePage
